// Canonical motion tokens for the dashboard-agent surface. One source of truth
// so every animated surface reads as one motion language. Tuned against
// Material 3 motion tokens + web.dev performance guidance (2026 UI review).
//
// Rules every consumer follows: animate transform + opacity only; entrances
// decelerate (EASE.out), exits accelerate and run faster (EASE.in); reserve
// EASE.emphasized for the single shared-element hero morph and the nudge.

type Bezier = [number, number, number, number];

/** Durations in seconds (framer-motion units). */
export const DURATION = {
  micro: 0.12, // hover / press / toggle / icon swap
  short: 0.2, // exits, dismissals, small reveals
  medium: 0.3, // entrances, panels, expand/collapse
  large: 0.4, // the one shared-element hero morph
} as const;

/** Named cubic-bezier easings. */
export const EASE = {
  standard: [0.2, 0, 0, 1] as Bezier, // on-screen movement (both ends visible)
  out: [0, 0, 0, 1] as Bezier, // entrances — decelerate in
  in: [0.3, 0, 1, 1] as Bezier, // exits — accelerate out
  emphasized: [0.05, 0.7, 0.1, 1] as Bezier, // hero morph + nudge settle
} as const;

/** Layout-morph spring — tighter than framer's default (100/10/1), which reads
 *  too bouncy/slow for an editorial dashboard. Used for the post-send ring. */
export const LAYOUT_SPRING = {
  type: "spring" as const,
  stiffness: 260,
  damping: 30,
  mass: 1,
};

/** Streamed-section reveal — opacity + small translateY, GPU-friendly. */
export const SECTION_REVEAL = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
} as const;

/** Stagger between sibling sections as they arrive (seconds). */
export const SECTION_STAGGER = 0.06;
